// React
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Redux
import { useDispatch } from "react-redux";
import { setPatient } from "Store/PatientSlice";

// MUI
import { Card, Box, Typography, Button, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";

// Styles
import { styles } from "./styles";

// icons
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";

// Components
import ReasonForVisitModal from "Components/ReasonForVisitModal";
import DeactivatePatientModal from "./DeactivatePatientModal";
import MFA from "Components/MFA/MFA";
import { useTranslation } from "react-i18next";

const PatientCards = (props) => {
  const { patients, fetchPatients } = props;
  const [openReasonForVisit, setOpenReasonForVisit] = useState(false);
  const [openMFA, setOpenMFA] = useState(false);
  const [onVerify, setOnVerify] = useState(null);
  const [currPatient, setCurrPatient] = useState(null);
  const [action, setAction] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const patientRef = useRef(null);

  const PatientCard = ({ patient }) => {
    const [deletePatientModal, setDeletePatientModal] = useState(false);
    const verified2FA =
      sessionStorage.getItem(`2FA${patient.id}`) === "verified";
    const getInitials = () =>
      patient.firstName.charAt(0) + patient.lastName.charAt(0);

    const handleNavigation = (route, state) => {
      setOpenMFA(false);
      navigate(route, state);
    };

    const handleMFAClose = () => {
      setOpenMFA(false);
    };

    useEffect(() => {
      if (
        !openMFA &&
        currPatient?.id === patient.id &&
        verified2FA &&
        action === "DELETE"
      ) {
        setDeletePatientModal(true);
      }
    }, [verified2FA, patient.id]);

    const handleMFAAction = (MFAAction, actionType = null, ...args) => {
      dispatch(setPatient(patient));
      setCurrPatient(patient);
      setAction(actionType);

      if (verified2FA) {
        MFAAction(...args);
      } else {
        setOnVerify(() => () => MFAAction(...args));
        setOpenMFA(true);
      }
    };

    const handleDeleteClose = () => {
      setDeletePatientModal(false);
    };

    const handleDeleteConfirm = () => {
      handleDeleteClose();
      fetchPatients();
    };

    return (
      <Card sx={styles.card}>
        <Box sx={styles.header} />
        <Box sx={styles.logoBox}>
          <Box sx={styles.logoBorder} />
          <Box sx={styles.logo}>
            <Typography sx={styles.initials}>{getInitials()}</Typography>
          </Box>
        </Box>
        <Grid container sx={styles.grid}>
          <Grid size={3}>
            <IconButton
              sx={styles.createIcon}
              onClick={() => {
                handleMFAAction(handleNavigation, null, "/PatientForm", {
                  state: { patientRef },
                });
              }}
            >
              <CreateIcon sx={styles.responsiveIcon} />
            </IconButton>
          </Grid>
          <Grid size={6} />
          <Grid size={3}>
            <IconButton
              sx={styles.deleteIcon}
              onClick={() => {
                handleMFAAction(handleMFAClose, "DELETE");
              }}
            >
              <DeleteIcon sx={styles.responsiveIcon} />
            </IconButton>
          </Grid>
          <Grid size={12} sx={styles.grid}>
            <Typography sx={styles.name}>
              {patient.firstName + " " + patient.lastName}
            </Typography>
          </Grid>
          <Grid size={12} sx={styles.grid}></Grid>
          <Grid container sx={styles.apptGrid}></Grid>
          <Grid container spacing={1.5} sx={styles.buttonContainer}>
            <Grid size={1} />
            <Grid size={10}>
              <Button
                variant="outlined"
                sx={styles.secondaryButton}
                onClick={() => {
                  handleMFAAction(
                    handleNavigation,
                    null,
                    "/AppointmentManagement"
                  );
                }}
              >
                {t("patientCards.appointments")}
              </Button>
            </Grid>
            <Grid size={1} />
            <Grid size={12}>
              <Button
                variant="contained"
                sx={styles.primaryButton}
                onClick={() => {
                  setOpenReasonForVisit(true);
                  dispatch(setPatient(patient));
                }}
              >
                {t("patientCards.bookAppointment")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <DeactivatePatientModal
          open={deletePatientModal}
          patient={patient}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
        />
      </Card>
    );
  };

  return (
    <Box sx={styles.cardBox}>
      <Grid spacing={{ xs: 3, md: 4, lg: 6 }} container sx={styles.cardGrid}>
        {patients.length > 0 ? (
          patients.map((patient) => (
            <PatientCard key={patient.id} patient={patient} />
          ))
        ) : (
          <Typography variant="h6" align="center" sx={styles.noPatients}>
            {t("patientCards.noPatients")}
          </Typography>
        )}
      </Grid>
      {openReasonForVisit && (
        <ReasonForVisitModal setOpenReasonForVisit={setOpenReasonForVisit} />
      )}
      {openMFA && (
        <MFA
          open={openMFA}
          onClose={() => setOpenMFA(false)}
          onVerify={onVerify}
        />
      )}
    </Box>
  );
};

export default PatientCards;
